import React, {useState} from 'react';

import AnalysisSelector from 'analysis-tool/AnalysisSelector';
import * as Analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {
  ANALYTICS_MODULE,
  ActionOrigin,
  AnalyticsMetadataForContext,
  COMMON_ANALYTICS_ACTIONS,
} from 'constants/AnalyticsConstants';
import FormGroup from 'ui-library/bs/FormGroup';
import {ButtonStyle} from 'ui-library/buttons/Button';
import useFormInput from 'ui-library/hooks/useFormInput';
import useOnMount from 'ui-library/hooks/useOnMount';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import TextField from 'ui-library/inputs/TextField';
import Modal from 'ui-library/modals/Modal';
import {withLoadingScreen} from 'util/Promises';

function onlyWhiteSpace(str: string): boolean {
  return str.trim().length === 0;
}

export interface AddDatasetToAnalysisConfiguration {
  dataFrameName: string;
  analysisId: string;
}

export interface AddDatasetToAnalysisConfigurationModalProps {
  onCreateDataFrame: (params: AddDatasetToAnalysisConfiguration) => Promise<unknown>;
  onRequestSubmit: () => void;
  onRequestClose: () => void;
  /** Text to prefill to the dataset name field */
  initialDatasetName?: string;
}

export default function AddDatasetToAnalysisConfigurationModal(
  props: AddDatasetToAnalysisConfigurationModalProps
): JSX.Element {
  const dataFrameNameFormInput = useFormInput(props.initialDatasetName ?? '');
  const dataFrameName = dataFrameNameFormInput.value;

  const [selectedAnalysisId, setSelectedAnalysisId] = useState<string | null>(null);

  const dataFrameNameValid = dataFrameName != null && !onlyWhiteSpace(dataFrameName);
  const analysisValid = selectedAnalysisId != null; // TODO
  const canSubmit = dataFrameNameValid && analysisValid;

  const analyticsMetadata: AnalyticsMetadataForContext = {
    actionOrigin: ActionOrigin.NOTEBOOK,
    modalType: 'create_dataset',
  };
  const analyticsModule = ANALYTICS_MODULE.ANALYSIS_TOOL;

  async function handleRequestSubmit(): Promise<void> {
    assert(canSubmit, 'Unexpectedly creating analysis with null dataset name or analysis');
    await withLoadingScreen(
      props.onCreateDataFrame({dataFrameName, analysisId: selectedAnalysisId}),
      'Adding analysis table to analysis...'
    );
    props.onRequestSubmit();
  }

  useOnMount(() => {
    Analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.OPENED_MODAL, analyticsModule, analyticsMetadata);
  });

  return (
    <Modal
      title="Add to existing analysis"
      onRequestSubmit={() => {
        Analytics.trackEventWithModule(
          COMMON_ANALYTICS_ACTIONS.SUBMITTED_MODAL,
          analyticsModule,
          analyticsMetadata
        );
        handleRequestSubmit();
      }}
      onRequestClose={() => {
        Analytics.trackEventWithModule(
          COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL,
          analyticsModule,
          analyticsMetadata
        );
        props.onRequestClose();
      }}
      buttons={[
        {
          submit: true,
          text: 'Create',
          style: ButtonStyle.SECONDARY,
          disabled: !canSubmit,
        },
      ]}
    >
      <FormGroup>
        <FieldLabel label="Analysis" />
        <AnalysisSelector
          selectedAnalysisId={selectedAnalysisId}
          onSelect={(analysis) => {
            setSelectedAnalysisId(analysis.id);
          }}
          disableReadOnlyAnalyses={true}
          hideArchivedAnalyses={true}
        />
      </FormGroup>
      <FormGroup>
        <TextField
          label="Analysis table name"
          autoFocus={false}
          dataTestAttrs={{component: 'CreateAnalysisModal', element: 'dataFrameTextField'}}
          {...dataFrameNameFormInput}
        />
      </FormGroup>
    </Modal>
  );
}
